import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { IconShirt, IconBell } from '@tabler/icons-react'
import { useAuth, getInitials } from '../../context/AuthContext.tsx'

type TopNavProps = {
  unreadCount?: number
}

const navItems = [
  { to: '/locker-room', label: 'My Locker Room' },
  { to: '/kit-collections', label: 'Kit Collections' },
  { to: '/community', label: 'Community' },
]

export function TopNav({ unreadCount = 0 }: TopNavProps) {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { profile, logout } = useAuth()

  const initials = getInitials(profile)

  return (
    <header className="flex items-center h-12 px-4 bg-surface-overlay border-b border-border-default shrink-0 z-50">
      {/* Logo */}
      <div className="flex items-center gap-2 mr-7">
        <IconShirt size={18} className="text-brand-orange" />
        <span className="text-sm font-semibold tracking-tight">
          Gear<span className="text-brand-orange">Locker</span>
        </span>
      </div>

      {/* Primary nav */}
      <nav className="flex items-center gap-0.5 flex-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                'px-3 py-1.5 rounded-md text-xs transition-colors',
                isActive
                  ? 'text-brand-orange bg-surface-raised'
                  : 'text-zinc-500 hover:text-zinc-200 hover:bg-surface-raised',
              ].join(' ')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Notifications */}
        <button
          className="relative p-1.5 text-zinc-500 hover:text-zinc-200 transition-colors"
          aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ''}`}
        >
          <IconBell size={18} />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-brand-orange rounded-full border-2 border-surface-overlay" />
          )}
        </button>

        {/* User avatar */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-7 h-7 rounded-full bg-amber-950 border border-brand-orange flex items-center justify-center text-brand-orange text-xs font-semibold"
            aria-label="User menu"
          >
            {initials}
          </button>

          {/* Dropdown */}
          {showUserMenu && (
            <div className="absolute right-0 top-9 w-48 bg-surface-overlay border border-border-default rounded-md shadow-lg z-50 py-1">
              {profile?.displayName && (
                <div className="px-3 py-1.5 text-xs font-medium text-zinc-200 border-b border-border-default truncate">
                  {profile.displayName}
                </div>
              )}
              {profile?.email && (
                <div className="px-3 py-1.5 text-xs text-zinc-500 border-b border-border-default truncate">
                  {profile.email}
                </div>
              )}
              <NavLink
                to="/profile"
                onClick={() => setShowUserMenu(false)}
                className="block w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-surface-raised hover:text-zinc-100 transition-colors"
              >
                Profile
              </NavLink>
              <button
                onClick={() => logout()}
                className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-surface-raised hover:text-zinc-100 transition-colors"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
